import {
  getMediaFilterGrainAsset,
  getMediaFilterLutAsset,
  getMediaFilterOverlayAsset,
  getMediaFilterPreset,
  hasMediaFilterRecipeEffects,
  MEDIA_FILTER_DEFAULT_LUT_SIZE,
  MediaFilterBlendMode,
  MediaFilterGrainAssetDefinition,
  MediaFilterLutAssetDefinition,
  MediaFilterOverlayAssetDefinition,
  MediaFilterPresetId,
  normalizeMediaFilterPresetId,
} from './media-filter-presets';

const CHANNEL_MAX = 255;
const MID_CHANNEL = 0.5;
const EPSILON = 0.0001;

interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

export interface MediaFilterLutData {
  size: number;
  values: Float32Array;
  textureCanvas: HTMLCanvasElement;
}

const LUT_CACHE = new Map<string, MediaFilterLutData>();
const OVERLAY_TEXTURE_CACHE = new Map<string, HTMLCanvasElement>();
const GRAIN_TEXTURE_CACHE = new Map<string, HTMLCanvasElement>();

export function hasRenderableMediaFilterEffects(presetId?: string | null): boolean {
  return hasMediaFilterRecipeEffects(presetId);
}

export function renderCanvasWithMediaFilter(
  sourceCanvas: HTMLCanvasElement,
  presetId: MediaFilterPresetId,
  targetCanvas?: HTMLCanvasElement,
): HTMLCanvasElement {
  if (!hasRenderableMediaFilterEffects(presetId)) {
    return sourceCanvas;
  }

  const filteredCanvas = targetCanvas || document.createElement('canvas');
  filteredCanvas.width = sourceCanvas.width;
  filteredCanvas.height = sourceCanvas.height;
  const filteredContext = get2dContext(filteredCanvas);
  if (!filteredContext) {
    return sourceCanvas;
  }

  filteredContext.clearRect(0, 0, filteredCanvas.width, filteredCanvas.height);
  filteredContext.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height);
  applyMediaFilterToCanvasInPlace(filteredContext, sourceCanvas.width, sourceCanvas.height, presetId);

  return filteredCanvas;
}

export function applyMediaFilterToCanvasInPlace(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  presetId?: string | null,
): void {
  const normalizedPresetId = normalizeMediaFilterPresetId(presetId);
  if (!hasRenderableMediaFilterEffects(normalizedPresetId)) {
    return;
  }

  const preset = getMediaFilterPreset(normalizedPresetId);
  const lutData = getMediaFilterLutData(normalizedPresetId);
  const imageData = context.getImageData(0, 0, width, height);
  const data = imageData.data;

  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] <= 0) {
      continue;
    }

    const transformed = sampleLutColor(
      lutData,
      data[index] / CHANNEL_MAX,
      data[index + 1] / CHANNEL_MAX,
      data[index + 2] / CHANNEL_MAX,
    );

    data[index] = clampByte(transformed.red * CHANNEL_MAX);
    data[index + 1] = clampByte(transformed.green * CHANNEL_MAX);
    data[index + 2] = clampByte(transformed.blue * CHANNEL_MAX);
  }

  context.putImageData(imageData, 0, 0);

  const overlayCanvas = getMediaFilterOverlayTextureCanvas(normalizedPresetId, width, height);
  if (overlayCanvas && preset.overlayBlendMode && (preset.overlayOpacity || 0) > 0) {
    applyTextureBlendInPlace(context, overlayCanvas, preset.overlayBlendMode, preset.overlayOpacity || 0);
  }

  const grainCanvas = getMediaFilterGrainTextureCanvas(normalizedPresetId, width, height);
  if (grainCanvas && preset.grainBlendMode && (preset.grainOpacity || 0) > 0) {
    applyTextureBlendInPlace(context, grainCanvas, preset.grainBlendMode, preset.grainOpacity || 0);
  }
}

export function computeContainRect(
  sourceWidth: number,
  sourceHeight: number,
  containerWidth: number,
  containerHeight: number,
): { width: number; height: number; left: number; top: number } | null {
  if (!sourceWidth || !sourceHeight || !containerWidth || !containerHeight) {
    return null;
  }

  const scale = Math.min(containerWidth / sourceWidth, containerHeight / sourceHeight);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const left = Math.max(0, Math.round((containerWidth - width) / 2));
  const top = Math.max(0, Math.round((containerHeight - height) / 2));

  return { width, height, left, top };
}

export function getMediaFilterLutData(presetId?: string | null): MediaFilterLutData {
  const preset = getMediaFilterPreset(presetId);
  const cacheKey = preset.lutAsset;
  const cached = LUT_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  const asset = getMediaFilterLutAsset(preset.lutAsset);
  const size = MEDIA_FILTER_DEFAULT_LUT_SIZE || 32;
  const values = new Float32Array(size * size * size * 3);

  for (let blueIndex = 0; blueIndex < size; blueIndex += 1) {
    for (let greenIndex = 0; greenIndex < size; greenIndex += 1) {
      for (let redIndex = 0; redIndex < size; redIndex += 1) {
        const color = applyLutRecipeToColor(
          redIndex / (size - 1),
          greenIndex / (size - 1),
          blueIndex / (size - 1),
          asset,
        );
        const offset = (((blueIndex * size * size) + (greenIndex * size) + redIndex) * 3);
        values[offset] = color.red;
        values[offset + 1] = color.green;
        values[offset + 2] = color.blue;
      }
    }
  }

  const textureCanvas = buildLutTextureCanvas(values, size);
  const lutData = { size, values, textureCanvas };
  LUT_CACHE.set(cacheKey, lutData);

  return lutData;
}

export function getMediaFilterOverlayTextureCanvas(
  presetId?: string | null,
  width?: number,
  height?: number,
): HTMLCanvasElement | null {
  const preset = getMediaFilterPreset(presetId);
  const asset = getMediaFilterOverlayAsset(preset.overlayAsset);
  if (!asset || !width || !height || !preset.overlayBlendMode) {
    return null;
  }

  const cacheKey = [
    preset.overlayAsset,
    preset.overlayBlendMode,
    width,
    height,
  ].join('|');
  const cached = OVERLAY_TEXTURE_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  const canvas = generateOverlayTextureCanvas(asset, preset.overlayBlendMode, width, height);
  OVERLAY_TEXTURE_CACHE.set(cacheKey, canvas);

  return canvas;
}

export function getMediaFilterGrainTextureCanvas(
  presetId?: string | null,
  width?: number,
  height?: number,
): HTMLCanvasElement | null {
  const preset = getMediaFilterPreset(presetId);
  const asset = getMediaFilterGrainAsset(preset.grainAsset);
  if (!asset || !width || !height || !preset.grainBlendMode) {
    return null;
  }

  const grainScale = preset.grainScale || 1;
  const cacheKey = [
    preset.grainAsset,
    preset.grainBlendMode,
    width,
    height,
    grainScale,
  ].join('|');
  const cached = GRAIN_TEXTURE_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  const canvas = generateGrainTextureCanvas(asset, preset.grainBlendMode, width, height, grainScale);
  GRAIN_TEXTURE_CACHE.set(cacheKey, canvas);

  return canvas;
}

function applyTextureBlendInPlace(
  context: CanvasRenderingContext2D,
  textureCanvas: HTMLCanvasElement,
  blendMode: MediaFilterBlendMode,
  opacity: number,
): void {
  const width = textureCanvas.width;
  const height = textureCanvas.height;
  const baseImageData = context.getImageData(0, 0, width, height);
  const overlayContext = get2dContext(textureCanvas);
  if (!overlayContext) {
    return;
  }

  const textureData = overlayContext.getImageData(0, 0, width, height).data;
  const baseData = baseImageData.data;
  const normalizedOpacity = clamp01(opacity);

  for (let index = 0; index < baseData.length; index += 4) {
    if (baseData[index + 3] <= 0) {
      continue;
    }

    const base = {
      red: baseData[index] / CHANNEL_MAX,
      green: baseData[index + 1] / CHANNEL_MAX,
      blue: baseData[index + 2] / CHANNEL_MAX,
    };
    const texture = {
      red: textureData[index] / CHANNEL_MAX,
      green: textureData[index + 1] / CHANNEL_MAX,
      blue: textureData[index + 2] / CHANNEL_MAX,
    };

    const blended = blendRgbNormalized(base, texture, blendMode);
    baseData[index] = clampByte(mix(base.red, blended.red, normalizedOpacity) * CHANNEL_MAX);
    baseData[index + 1] = clampByte(mix(base.green, blended.green, normalizedOpacity) * CHANNEL_MAX);
    baseData[index + 2] = clampByte(mix(base.blue, blended.blue, normalizedOpacity) * CHANNEL_MAX);
  }

  context.putImageData(baseImageData, 0, 0);
}

function generateOverlayTextureCanvas(
  asset: MediaFilterOverlayAssetDefinition,
  blendMode: MediaFilterBlendMode,
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = get2dContext(canvas);
  if (!context) {
    return canvas;
  }

  const imageData = context.createImageData(width, height);
  const data = imageData.data;
  const aspect = height > 0 ? width / height : 1;
  const baseColor = parseHexColor(asset.baseColor || formatNeutralColor(blendMode));

  for (let y = 0; y < height; y += 1) {
    const normalizedY = (y + 0.5) / height;
    for (let x = 0; x < width; x += 1) {
      const normalizedX = (x + 0.5) / width;
      let color = { ...baseColor };

      asset.spots.forEach((spot) => {
        const dx = normalizedX - spot.x;
        const dy = (normalizedY - spot.y) * aspect;
        const distance = Math.sqrt((dx * dx) + (dy * dy)) / Math.max(spot.radius, EPSILON);
        if (distance >= 1) {
          return;
        }

        const influence = Math.pow(1 - distance, Math.max(spot.softness, EPSILON));
        color = mixRgb(color, parseHexColor(spot.color), clamp01(spot.opacity * influence));
      });

      asset.bands.forEach((band) => {
        const influence = resolveBandInfluence(normalizedX, normalizedY, band, aspect);
        if (influence <= 0) {
          return;
        }

        color = mixRgb(color, parseHexColor(band.color), clamp01(band.opacity * influence));
      });

      const offset = ((y * width) + x) * 4;
      data[offset] = clampByte(color.red * CHANNEL_MAX);
      data[offset + 1] = clampByte(color.green * CHANNEL_MAX);
      data[offset + 2] = clampByte(color.blue * CHANNEL_MAX);
      data[offset + 3] = CHANNEL_MAX;
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
}

function generateGrainTextureCanvas(
  asset: MediaFilterGrainAssetDefinition,
  blendMode: MediaFilterBlendMode,
  width: number,
  height: number,
  grainScale: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = get2dContext(canvas);
  if (!context) {
    return canvas;
  }

  const imageData = context.createImageData(width, height);
  const data = imageData.data;
  const neutral = resolveNeutralColor(blendMode);
  const scale = Math.max(0.4, grainScale);
  const cellSize = Math.max(0.75, asset.cellSize * scale);

  for (let y = 0; y < height; y += 1) {
    const noiseY = Math.floor(y / cellSize);
    for (let x = 0; x < width; x += 1) {
      const noiseX = Math.floor(x / cellSize);
      const noiseValue = resolveDeterministicNoise(noiseX, noiseY, asset.seed);
      const grainDelta = ((noiseValue - 0.5) * 2) * clamp01(asset.intensity);
      const channel = clampByte((neutral + (grainDelta * 0.5)) * CHANNEL_MAX);
      const offset = ((y * width) + x) * 4;
      data[offset] = channel;
      data[offset + 1] = channel;
      data[offset + 2] = channel;
      data[offset + 3] = CHANNEL_MAX;
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
}

function applyLutRecipeToColor(
  red: number,
  green: number,
  blue: number,
  asset: MediaFilterLutAssetDefinition,
): RgbColor {
  let current = { red, green, blue };

  if (asset.grayscale > EPSILON) {
    const grayscaleLuma = resolveLumaNormalized(current.red, current.green, current.blue);
    current = {
      red: mix(current.red, grayscaleLuma, asset.grayscale),
      green: mix(current.green, grayscaleLuma, asset.grayscale),
      blue: mix(current.blue, grayscaleLuma, asset.grayscale),
    };
  }

  if (asset.sepia > EPSILON) {
    const sepia = {
      red: (current.red * 0.393) + (current.green * 0.769) + (current.blue * 0.189),
      green: (current.red * 0.349) + (current.green * 0.686) + (current.blue * 0.168),
      blue: (current.red * 0.272) + (current.green * 0.534) + (current.blue * 0.131),
    };
    current = {
      red: mix(current.red, sepia.red, asset.sepia),
      green: mix(current.green, sepia.green, asset.sepia),
      blue: mix(current.blue, sepia.blue, asset.sepia),
    };
  }

  const matrix = asset.channelMix;
  const mixedRed = (current.red * matrix[0][0]) + (current.green * matrix[0][1]) + (current.blue * matrix[0][2]);
  const mixedGreen = (current.red * matrix[1][0]) + (current.green * matrix[1][1]) + (current.blue * matrix[1][2]);
  const mixedBlue = (current.red * matrix[2][0]) + (current.green * matrix[2][1]) + (current.blue * matrix[2][2]);
  current = {
    red: mixedRed,
    green: mixedGreen,
    blue: mixedBlue,
  };

  current = {
    red: current.red * asset.brightness,
    green: current.green * asset.brightness,
    blue: current.blue * asset.brightness,
  };

  current = {
    red: ((current.red - MID_CHANNEL) * asset.contrast) + MID_CHANNEL,
    green: ((current.green - MID_CHANNEL) * asset.contrast) + MID_CHANNEL,
    blue: ((current.blue - MID_CHANNEL) * asset.contrast) + MID_CHANNEL,
  };

  if (Math.abs(asset.saturation - 1) > EPSILON) {
    const saturationLuma = resolveLumaNormalized(current.red, current.green, current.blue);
    current = {
      red: saturationLuma + ((current.red - saturationLuma) * asset.saturation),
      green: saturationLuma + ((current.green - saturationLuma) * asset.saturation),
      blue: saturationLuma + ((current.blue - saturationLuma) * asset.saturation),
    };
  }

  const luma = resolveLumaNormalized(current.red, current.green, current.blue);
  current = {
    red: current.red + (asset.lift[0] * (1 - current.red) * (1 - luma)),
    green: current.green + (asset.lift[1] * (1 - current.green) * (1 - luma)),
    blue: current.blue + (asset.lift[2] * (1 - current.blue) * (1 - luma)),
  };

  current = {
    red: Math.pow(clamp01(current.red), 1 / Math.max(asset.gamma[0], EPSILON)),
    green: Math.pow(clamp01(current.green), 1 / Math.max(asset.gamma[1], EPSILON)),
    blue: Math.pow(clamp01(current.blue), 1 / Math.max(asset.gamma[2], EPSILON)),
  };

  if (asset.tintAmount > EPSILON) {
    const tint = parseHexColor(asset.tint);
    current = {
      red: mix(current.red, current.red * tint.red, asset.tintAmount),
      green: mix(current.green, current.green * tint.green, asset.tintAmount),
      blue: mix(current.blue, current.blue * tint.blue, asset.tintAmount),
    };
  }

  return {
    red: clamp01(current.red),
    green: clamp01(current.green),
    blue: clamp01(current.blue),
  };
}

function buildLutTextureCanvas(values: Float32Array, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size * size;
  canvas.height = size;
  const context = get2dContext(canvas);
  if (!context) {
    return canvas;
  }

  const imageData = context.createImageData(canvas.width, canvas.height);
  const data = imageData.data;

  for (let blueIndex = 0; blueIndex < size; blueIndex += 1) {
    for (let greenIndex = 0; greenIndex < size; greenIndex += 1) {
      for (let redIndex = 0; redIndex < size; redIndex += 1) {
        const sourceOffset = (((blueIndex * size * size) + (greenIndex * size) + redIndex) * 3);
        const x = (blueIndex * size) + redIndex;
        const y = greenIndex;
        const targetOffset = ((y * canvas.width) + x) * 4;
        data[targetOffset] = clampByte(values[sourceOffset] * CHANNEL_MAX);
        data[targetOffset + 1] = clampByte(values[sourceOffset + 1] * CHANNEL_MAX);
        data[targetOffset + 2] = clampByte(values[sourceOffset + 2] * CHANNEL_MAX);
        data[targetOffset + 3] = CHANNEL_MAX;
      }
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
}

function sampleLutColor(
  lutData: MediaFilterLutData,
  red: number,
  green: number,
  blue: number,
): RgbColor {
  const size = lutData.size;
  const maxIndex = size - 1;
  const redPosition = clamp01(red) * maxIndex;
  const greenPosition = clamp01(green) * maxIndex;
  const bluePosition = clamp01(blue) * maxIndex;

  const redLow = Math.floor(redPosition);
  const redHigh = Math.min(maxIndex, redLow + 1);
  const greenLow = Math.floor(greenPosition);
  const greenHigh = Math.min(maxIndex, greenLow + 1);
  const blueLow = Math.floor(bluePosition);
  const blueHigh = Math.min(maxIndex, blueLow + 1);

  const redMix = redPosition - redLow;
  const greenMix = greenPosition - greenLow;
  const blueMix = bluePosition - blueLow;

  const c000 = readLutEntry(lutData.values, size, redLow, greenLow, blueLow);
  const c100 = readLutEntry(lutData.values, size, redHigh, greenLow, blueLow);
  const c010 = readLutEntry(lutData.values, size, redLow, greenHigh, blueLow);
  const c110 = readLutEntry(lutData.values, size, redHigh, greenHigh, blueLow);
  const c001 = readLutEntry(lutData.values, size, redLow, greenLow, blueHigh);
  const c101 = readLutEntry(lutData.values, size, redHigh, greenLow, blueHigh);
  const c011 = readLutEntry(lutData.values, size, redLow, greenHigh, blueHigh);
  const c111 = readLutEntry(lutData.values, size, redHigh, greenHigh, blueHigh);

  const lowBlue = mixRgb(
    mixRgb(c000, c100, redMix),
    mixRgb(c010, c110, redMix),
    greenMix,
  );
  const highBlue = mixRgb(
    mixRgb(c001, c101, redMix),
    mixRgb(c011, c111, redMix),
    greenMix,
  );

  return mixRgb(lowBlue, highBlue, blueMix);
}

function readLutEntry(
  values: Float32Array,
  size: number,
  redIndex: number,
  greenIndex: number,
  blueIndex: number,
): RgbColor {
  const offset = (((blueIndex * size * size) + (greenIndex * size) + redIndex) * 3);
  return {
    red: values[offset],
    green: values[offset + 1],
    blue: values[offset + 2],
  };
}

export function blendRgbNormalized(base: RgbColor, overlay: RgbColor, blendMode: MediaFilterBlendMode): RgbColor {
  return {
    red: blendChannelNormalized(base.red, overlay.red, blendMode),
    green: blendChannelNormalized(base.green, overlay.green, blendMode),
    blue: blendChannelNormalized(base.blue, overlay.blue, blendMode),
  };
}

function blendChannelNormalized(base: number, overlay: number, blendMode: MediaFilterBlendMode): number {
  switch (blendMode) {
    case 'screen':
      return 1 - ((1 - base) * (1 - overlay));
    case 'multiply':
      return base * overlay;
    case 'overlay':
      return base < 0.5
        ? 2 * base * overlay
        : 1 - (2 * (1 - base) * (1 - overlay));
    case 'soft-light':
      return resolveSoftLightNormalized(base, overlay);
    case 'source-over':
    default:
      return overlay;
  }
}

function resolveSoftLightNormalized(base: number, overlay: number): number {
  if (overlay <= 0.5) {
    return base - ((1 - (2 * overlay)) * base * (1 - base));
  }

  const curve = base <= 0.25
    ? (((16 * base) - 12) * base + 4) * base
    : Math.sqrt(base);

  return base + (((2 * overlay) - 1) * (curve - base));
}

function resolveBandInfluence(
  normalizedX: number,
  normalizedY: number,
  band: { x1: number; y1: number; x2: number; y2: number; width: number; softness: number },
  aspect: number,
): number {
  const pointX = normalizedX;
  const pointY = normalizedY * aspect;
  const startX = band.x1;
  const startY = band.y1 * aspect;
  const endX = band.x2;
  const endY = band.y2 * aspect;
  const deltaX = endX - startX;
  const deltaY = endY - startY;
  const segmentLengthSquared = (deltaX * deltaX) + (deltaY * deltaY);

  if (segmentLengthSquared <= EPSILON) {
    return 0;
  }

  const projection = clamp01((((pointX - startX) * deltaX) + ((pointY - startY) * deltaY)) / segmentLengthSquared);
  const closestX = startX + (projection * deltaX);
  const closestY = startY + (projection * deltaY);
  const distanceX = pointX - closestX;
  const distanceY = pointY - closestY;
  const distance = Math.sqrt((distanceX * distanceX) + (distanceY * distanceY));
  const width = Math.max(band.width, EPSILON);
  const falloff = Math.max(0, 1 - (distance / width));

  return Math.pow(falloff, Math.max(band.softness, EPSILON));
}

function resolveNeutralColor(blendMode: MediaFilterBlendMode): number {
  switch (blendMode) {
    case 'screen':
      return 0;
    case 'multiply':
      return 1;
    case 'overlay':
    case 'soft-light':
      return 0.5;
    case 'source-over':
    default:
      return 0;
  }
}

function formatNeutralColor(blendMode: MediaFilterBlendMode): string {
  const channel = clampByte(resolveNeutralColor(blendMode) * CHANNEL_MAX).toString(16).padStart(2, '0');
  return `#${channel}${channel}${channel}`;
}

function resolveDeterministicNoise(x: number, y: number, seed: number): number {
  const modulus = 2147483647;
  let value = ((x * 374761) + (y * 668265) + (seed * 1446647)) % modulus;
  value = (value * 48271) % modulus;
  value = (value * 69621) % modulus;
  return value / modulus;
}

function parseHexColor(color: string): RgbColor {
  const normalized = color.replace('#', '').trim();
  const expanded = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  return {
    red: (Number.parseInt(expanded.slice(0, 2), 16) || 0) / CHANNEL_MAX,
    green: (Number.parseInt(expanded.slice(2, 4), 16) || 0) / CHANNEL_MAX,
    blue: (Number.parseInt(expanded.slice(4, 6), 16) || 0) / CHANNEL_MAX,
  };
}

function resolveLumaNormalized(red: number, green: number, blue: number): number {
  return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
}

function mixRgb(base: RgbColor, target: RgbColor, amount: number): RgbColor {
  return {
    red: mix(base.red, target.red, amount),
    green: mix(base.green, target.green, amount),
    blue: mix(base.blue, target.blue, amount),
  };
}

function mix(base: number, target: number, amount: number): number {
  return base + ((target - base) * amount);
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(CHANNEL_MAX, Math.round(value)));
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function get2dContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  return canvas.getContext('2d', { willReadFrequently: true });
}
